/**
 * Reading and writing one small preference, safely.
 *
 * Every access is guarded the way `session.ts` guards its own, because
 * `localStorage` does not merely come back empty in a private window -- the
 * accessor itself throws, and an unguarded read in a provider takes the whole
 * app down at mount. A preference that cannot be persisted still works for the
 * life of the tab, which is the right failure.
 */

const PREFIX = "spf.";

export function readStored<T>(key: string, parse: (raw: string) => T | null): T | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    return parse(raw);
  } catch {
    // Anything can be in storage: an older format, a truncated write, someone
    // experimenting in the console. Unusable means "use the default".
    return null;
  }
}

export function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(PREFIX + key, value);
  } catch {
    // Not persisted; still in effect until reload.
  }
}

/** Parses a value that must be one of a fixed set, rejecting anything else. */
export function oneOf<const T extends readonly string[]>(
  allowed: T,
): (raw: string) => T[number] | null {
  return (raw) => (allowed.includes(raw) ? raw : null);
}
